import { PROD_PUBLIC_PATH, STAGING_PUBLIC_PATH } from './constants';
import { type EnvironmentConfig, envType } from './types';

/**
 * Determines the current environment type based on PUBLIC_PATH
 */
export const getEnvType = (): envType => {
	const isStaging = process.env.PUBLIC_PATH === STAGING_PUBLIC_PATH;
	const isProd = process.env.PUBLIC_PATH === PROD_PUBLIC_PATH;

	if (isStaging) {
		return envType.STAGING;
	}
	if (isProd) {
		return envType.PROD;
	}
	return envType.LOCAL;
};

/**
 * Gets the hostname based on environment
 */
export const getHostname = (): string => {
	return getEnvType() === envType.LOCAL
		? process.env.REMOTE_DOMAIN || ''
		: window.location.hostname;
};

/**
 * Gets comprehensive environment configuration
 */
export const getEnvironmentConfig = (): EnvironmentConfig => {
	const envTypeValue = getEnvType();

	return {
		type: envTypeValue,
		isLocal: envTypeValue === envType.LOCAL,
		isStaging: envTypeValue === envType.STAGING,
		isProd: envTypeValue === envType.PROD,
		isDev: envTypeValue === envType.DEV,
		hostname: getHostname(),
		remoteDomain: process.env.REMOTE_DOMAIN,
	};
};

/**
 * Checks if the current environment is production
 */
export const isProduction = (): boolean => {
	return getEnvType() === envType.PROD;
};

/**
 * Checks if the current environment is staging
 */
export const isStaging = (): boolean => {
	return getEnvType() === envType.STAGING;
};

/**
 * Checks if the current environment is local development
 */
export const isLocal = (): boolean => {
	return getEnvType() === envType.LOCAL;
};

/**
 * Checks if the current environment is development
 */
export const isDevelopment = (): boolean => {
	const env = getEnvType();
	return env === envType.LOCAL || env === envType.DEV;
};
